import { RegionShared } from "@1games-ru/region-shared";
import { useMemo } from "react";
import clsx from "clsx";
import { MAX_RPM, MAX_SPEED } from "@/core/constants/vehicleLimits";
import { EngineIcon, FuelIcon, KeyIcon, SeatbeltIcon } from "@/shared/icons";
import getCounterFormat from "@/domains/hud/helpers/getCounterFormat";
import IndicatorStandartRPM from "@/domains/hud/components/speedometer/indicator/IndicatorStandartRPM";

type Props = {
  className?: string;
} & typeof RegionShared.Cef.Requests.SetVehicleStateRequest;

export default function SpeedometerStandart({
  className,
  gear,
  speed,
  isEngine,
  isSeatBelt,
  isLocked,
  rpm,
  fuel,
  fuelCapacity,
}: Props) {
  const rmpPercent = ((rpm / MAX_RPM) * 100).toFixed(0);

  const isFuelLow = useMemo(() => fuel <= 5, [fuel]);
  const formattedFuel = useMemo(() => getCounterFormat(fuel, 2, "left"), [fuel])
    .map((item) => item.value)
    .join("");
  const formattedFuelCapacity = useMemo(
    () =>
      getCounterFormat(fuelCapacity, 2, "left")
        .map((item) => item.value)
        .join(""),
    [fuelCapacity],
  );

  const newGear = useMemo(() => {
    if (Number(gear) === 0) {
      return "N";
    }
    if (Number(gear) < 0) {
      return "R";
    }

    return String(gear);
  }, [gear]);

  return (
    <div className={clsx("flex items-stretch justify-end", className)}>
      <div className="flex items-center self-end translate-y-0.5">
        <FuelIcon size={20} className={isFuelLow ? "text-accent" : "text-white/50"} />
        <div className="flex items-center gap-0.5">
          <div
            className={clsx(
              "w-6 text-18 font-medium italic tabular-nums text-right",
              isFuelLow ? "text-accent" : "text-white",
            )}
          >
            {formattedFuel}
          </div>
          <div className="text-18 font-medium italic text-white/50">/</div>
          <div className="w-6 text-18 font-medium italic tabular-nums text-white/50 text-left">
            {formattedFuelCapacity}
          </div>
        </div>
      </div>
      <div className="grow ml-3">
        <div className="flex items-end justify-between">
          <span
            className={`w-8 h-8 flex items-center justify-center border-solid border-white border-2 
            rounded-full text-20 text-white font-medium leading-[0.75] italic tabular-nums`}
          >
            {newGear}
          </span>
          <span className="w-[175px] text-[90px] text-white font-medium leading-[0.75] text-right italic tabular-nums">
            {Math.min(MAX_SPEED, speed)}
          </span>
        </div>

        <div className="relative mt-4">
          <IndicatorStandartRPM className="absolute top-0 left-0 z-0" />
          <IndicatorStandartRPM
            fill
            className="absolute top-0 left-0 z-1"
            style={{
              clipPath: `polygon(0px 0px, ${rmpPercent}% 0%, ${rmpPercent}% 100%, 0% 100%)`,
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-0.5 ml-5">
        <EngineIcon size={28} className={isEngine ? "text-white" : "text-white/50"} />
        <SeatbeltIcon
          size={28}
          className={clsx("-translate-x-2", isSeatBelt ? "text-white" : "text-white/50")}
        />
        <KeyIcon
          size={28}
          className={clsx("-translate-x-4", isLocked ? "text-white" : "text-white/50")}
        />
      </div>
    </div>
  );
}
